/**
 * sectionStorage — pure localStorage-Persistenz fuer den Sektion-Collapsed-State.
 *
 * Defensives Parsen: bei korruptem oder fehlendem JSON wird Default zurueckgegeben.
 * Schema-Validierung: nur bekannte Keys mit boolean-Werten werden uebernommen.
 *
 * Phase 11: CollapsedState erweitert um huck/helo/glawel/sporttore (alle default expanded=false).
 *
 * Pure functions — kein Side-Effect ausser dem injizierten Storage. Tests koennen
 * ein Mock-Storage injizieren und arbeiten ohne jsdom (vitest environment: 'node').
 */

export interface CollapsedState {
  stuck: boolean;
  carrier: boolean;
  neverShipped: boolean;   // Phase 18
  waiting: boolean;
  manualOnly: boolean;
  done: boolean;
  // Phase 11: Lieferanten-Sektionen
  huck: boolean;
  helo: boolean;
  glawel: boolean;
  sporttore: boolean;
  // Phase 16: Lieferanten-Done-Sektionen (default collapsed)
  huckDone: boolean;
  heloDone: boolean;
  glawelDone: boolean;
  sporttoreDone: boolean;
}

export const DEFAULT_COLLAPSED: CollapsedState = {
  stuck: false,      // expanded — Stuck/Alarm-Sektion
  carrier: false,    // expanded — Carrier-Sektion
  neverShipped: false, // Phase 18: expanded (im Tab-Layout ohnehin immer offen)
  waiting: true,     // collapsed
  manualOnly: true,  // collapsed
  done: true,        // collapsed
  // Phase 11: Lieferanten-Sektionen — default expanded (CONTEXT.md)
  huck: false,
  helo: false,
  glawel: false,
  sporttore: false,
  // Phase 16: Lieferanten-Abgeschlossen — default COLLAPSED (Sub-Sektion, on-demand)
  huckDone: true,
  heloDone: true,
  glawelDone: true,
  sporttoreDone: true,
};

export const LOCAL_STORAGE_KEY = 'auftragsverfolgung.sectionCollapsed';

/**
 * Minimales Storage-Interface — kompatibel mit window.localStorage UND Mock-Implementations.
 */
export interface MinimalStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Default-Storage = globalThis.localStorage, oder null in Node-Tests. */
function defaultStorage(): MinimalStorage | null {
  if (typeof globalThis !== 'undefined' && 'localStorage' in globalThis) {
    return (globalThis as unknown as { localStorage: MinimalStorage }).localStorage;
  }
  return null;
}

/** Liest CollapsedState aus localStorage. Bei Fehler/Korruption: Default. */
export function readCollapsed(storage: MinimalStorage | null = defaultStorage()): CollapsedState {
  if (!storage) return { ...DEFAULT_COLLAPSED };
  try {
    const raw = storage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_COLLAPSED };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ...DEFAULT_COLLAPSED };
    }

    const result: CollapsedState = { ...DEFAULT_COLLAPSED };
    for (const key of Object.keys(DEFAULT_COLLAPSED) as (keyof CollapsedState)[]) {
      if (typeof parsed[key] === 'boolean') {
        result[key] = parsed[key];
      }
    }
    return result;
  } catch {
    return { ...DEFAULT_COLLAPSED };
  }
}

/** Schreibt CollapsedState nach localStorage. Silent bei Fehler (z.B. Storage Full). */
export function writeCollapsed(
  state: CollapsedState,
  storage: MinimalStorage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage kann full sein oder disabled — silent skippen, kein Crash der UI
  }
}
